import { useEffect, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { useI18n } from '../../i18n';
import {
  getTournament,
  listPartnerArticles,
  listPartners,
  type Article,
  type Partner,
} from '../../lib/content';

export function Partners() {
  const { t, localePath } = useI18n();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [revealed, setRevealed] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listPartners().catch(() => []), getTournament().catch(() => null)])
      .then(([ps, info]) => {
        setPartners(ps);
        if (info) setRevealed(info.partnersRevealed);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppLayout>
      <div class="bc-label">{t('header.tabs.partners')}</div>
      {loading ? (
        <div class="bc-spinner">{t('app.loading')}</div>
      ) : !revealed ? (
        <p class="bc-empty">{t('partnersDir.soon')}</p>
      ) : partners.length === 0 ? (
        <p class="bc-empty">{t('partnersDir.empty')}</p>
      ) : (
        <div class="pt-grid">
          {partners.map((p) => (
            <a key={p.id} class="pt-card" href={localePath(`/partners/${p.id}`)}>
              {p.logoPath ? (
                <img class={`pt-card__logo${p.whiteBg ? ' is-white' : ''}`} src={p.logoPath} alt={p.name} loading="lazy" />
              ) : (
                <div class="pt-card__logo pt-card__logo--ph">{p.name.slice(0, 2)}</div>
              )}
              <div class="pt-card__body">
                <strong>{p.name}</strong>
                <span class="pt-card__meta">{p.canton ?? ''}</span>
              </div>
              {p.openNow != null && (
                <span class={`pt-card__open${p.openNow ? ' is-open' : ''}`}>
                  {p.openNow ? t('partnersDir.open') : t('partnersDir.closed')}
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </AppLayout>
  );
}

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export function PartnerDetail({ id }: { id: string }) {
  const { t, localePath } = useI18n();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const pid = parseInt(id, 10);
    Promise.all([listPartners().catch(() => []), listPartnerArticles(pid).catch(() => [])])
      .then(([ps, arts]) => {
        setPartner(ps.find((p) => p.id === pid) ?? null);
        setArticles(arts);
      })
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <AppLayout>
      <a class="md__back" href={localePath('/partners')}>← {t('header.tabs.partners')}</a>
      {loading ? (
        <div class="bc-spinner">{t('app.loading')}</div>
      ) : !partner ? (
        <p class="bc-empty">{t('partnersDir.notfound')}</p>
      ) : (
        <div class="pd">
          <div class="pd__head">
            {partner.logoPath && (
              <img class={`pd__logo${partner.whiteBg ? ' is-white' : ''}`} src={partner.logoPath} alt={partner.name} />
            )}
            <div>
              <h1 class="bc-serif">{partner.name}</h1>
              <p class="pd__meta">{partner.address ?? partner.canton}</p>
              {partner.url && (
                <a class="pd__link" href={partner.url} target="_blank" rel="noreferrer">{partner.url}</a>
              )}
            </div>
          </div>

          {partner.hours?.length > 0 && (
            <div class="bc-card pd__hours">
              <span class="bc-label">{t('partnersDir.hours')}</span>
              {partner.hours.map((h, i) => (
                <div key={i} class="pd__hourrow">
                  <span>{t(`partnersDir.wd.${WEEKDAYS[h.weekday] ?? h.weekday}`)}</span>
                  <span class="bc-mono">{h.opensAt}–{h.closesAt}</span>
                </div>
              ))}
            </div>
          )}

          {articles.length > 0 && (
            <section>
              <span class="bc-label">{t('partnersDir.prizes')}</span>
              <div class="prizes__grid">
                {articles.map((a) => (
                  <div key={a.id} class="bc-card prize">
                    {a.imagePath && <img class="prize__img" src={a.imagePath} alt={a.title} loading="lazy" />}
                    <strong>{a.title}</strong>
                    {a.valueChf != null && <span class="prize__value">CHF {a.valueChf}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </AppLayout>
  );
}
